import { useCallback } from 'react'
import { Network, Server, Power, PowerOff, Plug, Unplug, RefreshCw } from 'lucide-react'
import type { Project, ReverseProxyEventType } from '../data/projectTypes'
import { useGetReverseProxyEventsQuery } from '../services/projectDataApi'

interface ReverseProxyEventsProps {
  project: Project
}

// Visual treatment + human label per lifecycle event. `tone` drives the dot/icon colour class.
const EVENT_META: Record<ReverseProxyEventType, { label: string; tone: 'up' | 'down' | 'connect' | 'disconnect' | 'neutral'; Icon: typeof Server }> = {
  provisioning: { label: 'Preparing connection', tone: 'neutral', Icon: Server },
  server_up: { label: 'Replay QA ready', tone: 'up', Icon: Power },
  server_down: { label: 'Replay QA stopped', tone: 'down', Icon: PowerOff },
  client_connected: { label: 'Local computer connected', tone: 'connect', Icon: Plug },
  client_disconnected: { label: 'Local computer disconnected', tone: 'disconnect', Icon: Unplug },
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

const POLL_MS = 10_000

export default function ReverseProxyEvents({ project }: ReverseProxyEventsProps) {
  const eventsQuery = useGetReverseProxyEventsQuery(project.id, {
    pollingInterval: POLL_MS,
    skipPollingIfUnfocused: true,
  })
  const events = eventsQuery.data?.events ?? []
  const loading = eventsQuery.isLoading
  const error = eventsQuery.error ? 'Could not load local computer connection history.' : null
  const refresh = useCallback(() => { void eventsQuery.refetch() }, [eventsQuery.refetch])

  return (
    <section className="settings-section" data-testid="reverse-proxy-events">
      <h3 className="settings-section__title">
        <Network size={16} aria-hidden="true" /> Local Computer Connection History
      </h3>
      <p className="settings-section__desc">
        See when Replay QA was ready and when your local computer connected or disconnected.
      </p>

      <div className="rp-events__toolbar">
        <button
          type="button"
          className="settings-section__test-btn"
          onClick={refresh}
          data-testid="reverse-proxy-events-refresh"
          data-analytics-event="reverse-proxy-events-refresh"
        >
          <RefreshCw size={14} aria-hidden="true" /> Refresh
        </button>
      </div>

      {error && (
        <p className="settings-section__test-result settings-section__test-result--error" role="alert">
          {error}
        </p>
      )}

      {loading && events.length === 0 ? (
        <p className="settings-section__desc" style={{ margin: 0 }}>Loading…</p>
      ) : events.length === 0 ? (
        <p className="settings-section__desc" data-testid="reverse-proxy-events-empty" style={{ margin: 0 }}>
          No local computer connection activity yet.
        </p>
      ) : (
        <ul className="rp-events__list" data-testid="reverse-proxy-events-list">
          {events.map(e => {
            const meta = EVENT_META[e.type] ?? { label: e.type, tone: 'neutral' as const, Icon: Server }
            const Icon = meta.Icon
            return (
              <li key={e.id} className="rp-events__item">
                <span className={`rp-events__icon rp-events__icon--${meta.tone}`}>
                  <Icon size={14} aria-hidden="true" />
                </span>
                <span className="rp-events__label">
                  {meta.label}
                </span>
                <time className="rp-events__time" dateTime={e.at}>{formatTimestamp(e.at)}</time>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
